import React, { useEffect, useState } from 'react';
import { Button, Form, Loader, Message } from 'common/lazy';
import PropTypes from 'prop-types';
import { inject } from 'mobx-react';

import { useClassName, useWindowSize, useFormAuthGate } from 'common/hooks';
import {
  getAccountPrefill,
  isPastDateField,
  getUserFields,
  getUserFieldOptions,
} from 'common/helpers';
import { getContentfulField, ContentfulRichText } from 'common/components';
import { parseTextBlock } from 'public/helpers/contentful/parser';
import Asset from './Asset';
import TextBlock from './TextBlock';
import { isValidEmail, isValidPhone } from 'utils/helpers/validations';
import { track } from 'utils/analytics';
import { DEFAULT_GROUP_ID, SMS_GROUP_ID } from 'common/const';
import { ControlledInputContainer } from 'public/components/ControlledInputContainer';
import { FieldContainer } from 'public/components/FieldContainer';
import PhoneNumberInput from 'public/components/Auth/PhoneNumberInput';
import { FormLoginGate } from 'public/components/FormLoginGate';

import { Spacer } from '../Spacer';
import Select from './Select';
import ModularDatePicker from './DatePicker';
import MultiSelect from './MultiSelect';
import Checkbox from './Checkbox';
import CheckboxCards from './CheckboxCards';

import './modular-form.less';

const DEFAULT_SUCCESS_MESSAGE =
  'Thank you for submitting the group request form! Our dream team will be in touch within 48 hours';

const getConditions = (form) => {
  const conditions =
    form.fields.conditions && getContentfulField(form.fields.conditions);

  return (conditions || []).map(({ fields }) => {
    return {
      fieldName: getContentfulField(
        getContentfulField(fields.field).fields.name
      ),
      values: getContentfulField(fields.values),
      sendToEmails: getContentfulField(fields.sendToEmails),
      sendToMonday: getContentfulField(fields.sendToMonday),
    };
  });
};

const getMatchingCondition = (form, data) => {
  const conditions = getConditions(form);
  if (!conditions?.length) return null;

  const formFields = getFormFields(form);
  const fields = Object.fromEntries(
    formFields
      .map(({ name, value }) => [name, value || data[name]])
      .filter(([key, value]) => key && value)
  );

  return conditions.find((condition) => {
    const value = fields[condition.fieldName];
    if (!value) return false;

    return condition.values.includes(value);
  });
};

const getOptionItems = (items) =>
  (items || []).map(({ fields }) => {
    const value = getContentfulField(fields.value);
    const file =
      fields.asset &&
      getContentfulField(getContentfulField(fields.asset).fields?.file);

    return {
      value,
      label: getContentfulField(fields.label) || value,
      ...(file?.url && { icon: `https:${file.url}` }),
    };
  });

const getSubscriptionGroups = (items) =>
  (items || []).map(({ fields }) => ({
    name: getContentfulField(fields.key),
    id: getContentfulField(fields.value),
  }));

const getSubscriptionGroupIds = (fields, values) => [
  ...new Set(
    fields
      .filter(({ type, name }) => type === 'checkbox' && values[name])
      .flatMap(({ subscriptionGroups }) => subscriptionGroups || [])
      .map(({ id }) => id)
      .filter(Boolean)
  ),
];

const getFormFields = (form) => {
  const fields = getContentfulField(form.fields.fields);

  return fields.map(({ fields }) => {
    return {
      label: getContentfulField(fields.label),
      placeholder: getContentfulField(fields.placeholder),
      type: getContentfulField(fields.type),
      name: getContentfulField(fields.name),
      userField: getContentfulField(fields.userField),
      pastDatesOnly: getContentfulField(fields.pastDatesOnly),
      mondayName: getContentfulField(fields.mondayName),
      brazeName: getContentfulField(fields.brazeName),
      value: getContentfulField(fields.value),
      required: getContentfulField(fields.required),
      description: getContentfulField(fields.description),
      display: getContentfulField(fields.display),
      options: getContentfulField(fields.options),
      optionItems: fields.optionItems
        ? getOptionItems(getContentfulField(fields.optionItems))
        : undefined,
      optionsSource: getContentfulField(fields.optionsSource),
      checkboxContent: fields.checkboxContent,
      subscriptionGroups: fields.subscriptionGroups
        ? getSubscriptionGroups(getContentfulField(fields.subscriptionGroups))
        : undefined,
      checkedByDefault: getContentfulField(fields.checkedByDefault),
      validations: fields.validations
        ? getContentfulField(fields.validations).map((validation) => {
            return {
              value: getContentfulField(validation.fields.value),
              type: getContentfulField(validation.fields.type),
              errorMessage: validation.fields.errorMessage,
            };
          })
        : [],
    };
  });
};

const getFieldValidations = (form) => {
  return getFormFields(form).reduce((validations, field) => {
    return {
      ...validations,
      [field.name]: [
        ...(field.required
          ? [
              {
                isInvalid: (fields) =>
                  field.type === 'multiselect'
                    ? !fields?.[field.name]?.length
                    : !fields[field.name],
                message: `${field.label} is required`,
              },
            ]
          : []),
        ...(field.type === 'email'
          ? [
              {
                isInvalid: (fields) => !isValidEmail(fields[field.name]),
                message: `Please provide a valid ${field.label}`,
              },
            ]
          : []),
        ...(field.type === 'phone'
          ? [
              {
                isInvalid: (fields) => !isValidPhone(fields[field.name]),
                message: `Please provide a valid ${[field.label]}`,
              },
            ]
          : []),
        ...(field.validations
          ? field.validations.map((validation) => {
              return {
                isInvalid: (fields) => {
                  if (validation.type === 'Greater Than') {
                    return (
                      Number(validation.value) >= Number(fields[field.name])
                    );
                  }

                  return false;
                },
                message: validation.errorMessage,
              };
            })
          : []),
      ],
    };
  }, {});
};

const getInitialFormData = (form) => {
  const fields = getFormFields(form);

  return Object.fromEntries(
    fields
      .filter((field) => field.type === 'checkbox')
      .map((field) => [field.name, field.checkedByDefault ? true : false])
  );
};

const FIELD_SPANS = { full: 6, half: 3, third: 2 };

const getDefaultWidth = ({ type, display }) =>
  ['textarea', 'checkbox'].includes(type) || display === 'cards'
    ? 'full'
    : 'half';

const ModularForm = (props) => {
  const {
    form,
    brazeTriggers,
    subscriptions,
    id,
    formSubmission,
    me,
    appSession,
  } = props;
  const backgroundTransparent = form?.fields?.backgroundTransparent
    ? getContentfulField(form.fields.backgroundTransparent)
    : false;
  const fieldLayout = form?.fields?.fieldLayout
    ? getContentfulField(form.fields.fieldLayout)
    : {};
  const requireLogin = form?.fields?.requireLogin
    ? getContentfulField(form.fields.requireLogin)
    : false;
  const submitAction = form?.fields?.submitAction
    ? getContentfulField(form.fields.submitAction)
    : undefined;
  const authGate = useFormAuthGate({ requireLogin, me, appSession });
  const [currentFocus, setCurrentFocus] = useState(null);
  const [enteredData, setFormData] = useState(() => getInitialFormData(form));
  const [fieldErrors, setFieldErrors] = useState({});
  const { isMobile } = useWindowSize();
  const className = useClassName('ModularForm');
  const [textBlock, setTextBlock] = useState(null);
  const [textCopies, setTextCopies] = useState([]);
  const [brazeCustomTriggers, setBrazeCustomTriggers] = useState(brazeTriggers);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mondayOptions, setMondayOptions] = useState({});
  const [sourceOptions, setSourceOptions] = useState({});

  useEffect(() => {
    if (form) {
      const textBlock =
        form?.fields?.textBlock &&
        parseTextBlock(getContentfulField(form.fields.textBlock));
      const textCopiesValues =
        form?.fields?.textCopies && getContentfulField(form.fields.textCopies);
      const brazeCustomAttribute =
        form?.fields?.brazeCustomAttribute &&
        getContentfulField(form.fields.brazeCustomAttribute);
      const brazeCustomEvent =
        form?.fields?.brazeCustomEvent &&
        getContentfulField(form.fields.brazeCustomEvent);
      const textCopies = textCopiesValues?.map(({ fields }) => {
        return {
          key: getContentfulField(fields?.key),
          value: getContentfulField(fields?.value),
        };
      });
      setTextBlock(textBlock);
      setTextCopies(textCopies);
      if (brazeCustomAttribute || brazeCustomEvent) {
        setBrazeCustomTriggers({
          ...brazeTriggers,
          ...(brazeCustomAttribute && {
            customAttributes: { [brazeCustomAttribute]: 'yes' },
          }),
          ...(brazeCustomEvent && { customEvent: brazeCustomEvent }),
        });
      }
    }

    const selectFieldsWithMondayName = getFormFields(form)
      .filter((field) => field.type === 'select' && field.mondayName)
      .map((field) => field.mondayName)
      .filter(Boolean);

    if (selectFieldsWithMondayName.length > 0) {
      formSubmission
        .getMondaySelectOptions(selectFieldsWithMondayName)
        .then((options) => {
          setMondayOptions(options);
        })
        .catch((error) => {
          console.error('Failed to fetch Monday options:', error);
        });
    }

    const optionsSources = [
      ...new Set(
        getFormFields(form)
          .filter(({ type }) => ['select', 'multiselect'].includes(type))
          .map(({ optionsSource }) => optionsSource)
          .filter(Boolean)
      ),
    ];

    if (optionsSources.length > 0) {
      formSubmission
        .getOptions(optionsSources)
        .then(setSourceOptions)
        .catch((error) => {
          console.error('Failed to fetch form options:', error);
        });
    }
  }, []);

  const getFieldItems = ({
    options,
    optionItems,
    mondayName,
    optionsSource,
  }) => {
    if (mondayName) return mondayOptions[mondayName] || [];
    if (optionsSource) return sourceOptions[optionsSource] || [];
    return optionItems || options;
  };

  const prefill = getAccountPrefill(
    authGate.user,
    getFormFields(form),
    getFieldItems
  );
  const formData = { ...prefill.editable, ...enteredData, ...prefill.locked };
  const isLocked = (name) => name in prefill.locked;

  const createSubscription = async (user, group) =>
    await subscriptions.create({ ...user, group });

  const getTextCopy = (key, defaultValue) => {
    const textCopy = textCopies?.find((textCopy) => textCopy.key === key);
    return textCopy?.value || defaultValue;
  };

  const sendToMonday = async () => {
    const condition = getMatchingCondition(form, formData);
    const shouldSend = condition
      ? condition.sendToMonday
      : getContentfulField(form.fields.sendToMonday);

    if (!shouldSend) return { sent: false };

    const fields = [
      ...getFormFields(form)
        .map(({ mondayName, name, value, type }) => {
          return {
            key: mondayName,
            value: value || formData[name],
            type,
          };
        })
        .filter(({ key, value, type }) => key && value && type),
    ];

    await formSubmission.sendToMonday(fields, true);
    return { sent: true };
  };

  const GLADLY_EMAIL = 'guestservices@americandream.com';

  const sendToEmails = async () => {
    const condition = getMatchingCondition(form, formData);
    const sendEmailTo = condition
      ? condition.sendToEmails
      : getContentfulField(form.fields.sendEmailTo);

    if (!sendEmailTo?.length) return { sent: false, emails: [] };

    const formFields = getFormFields(form);
    const fields = Object.fromEntries(
      formFields
        .filter(({ type }) => type !== 'hidden')
        .map(({ label, name, value }) => [label, value || formData[name]])
        .filter(([key, value]) => key && value)
    );

    if (!Object.keys(fields).length) return { sent: false, emails: [] };

    for (const to of sendEmailTo) {
      if (to === GLADLY_EMAIL) {
        await formSubmission.sendContactForm({
          name: `${fields['First Name'] || ''} ${
            fields['Last Name'] || ''
          }`.trim(),
          email: fields['Email'] || fields['Email Address'] || '',
          type: getContentfulField(form.fields.name),
          phone: fields['Phone'] || fields['Phone Number'] || '',
          message: fields['Message'] || '',
          emailTo: to,
        });
      } else {
        await formSubmission.notify(
          getContentfulField(form.fields.name),
          fields,
          to
        );
      }
    }

    return { sent: true, emails: sendEmailTo };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);

    const metadata = {
      sentToMonday: false,
      emailsSent: false,
      emailsSentTo: [],
      brazeCustomEvent: null,
      brazeCustomAttribute: null,
      mondayError: null,
      emailError: null,
    };

    const mondayPromise = sendToMonday()
      .then((result) => {
        if (result.sent) {
          metadata.sentToMonday = true;
        }
      })
      .catch((err) => {
        metadata.mondayError = err.message;
        console.error('Monday error:', err);
      });

    const emailPromise = sendToEmails()
      .then((result) => {
        if (result.sent) {
          metadata.emailsSent = true;
          metadata.emailsSentTo = result.emails;
        }
      })
      .catch((err) => {
        metadata.emailError = err.message;
        console.error('Email error:', err);
      });

    const brazePromise = sendToBraze()
      .then(() => {
        // Capture Braze custom event
        if (brazeCustomTriggers?.customEvent) {
          metadata.brazeCustomEvent = brazeCustomTriggers.customEvent;
        }
        // Capture Braze custom attribute
        if (brazeCustomTriggers?.customAttributes) {
          const attrKeys = Object.keys(brazeCustomTriggers.customAttributes);
          if (attrKeys.length > 0) {
            metadata.brazeCustomAttribute = attrKeys[0];
          }
        }
      })
      .catch((err) => {
        console.error('Braze error:', err);
      });

    const userFields = getUserFields(getFormFields(form), formData);
    const subscriptionGroupIds = getSubscriptionGroupIds(
      getFormFields(form),
      formData
    );

    Promise.all([mondayPromise, brazePromise, emailPromise])
      .then(() => {
        return formSubmission.save(formData, location.pathname, {
          ...metadata,
          ...(subscriptionGroupIds.length && { subscriptionGroupIds }),
          ...(submitAction && {
            action: submitAction,
            userFields,
            userFieldOptions: getUserFieldOptions(
              getFormFields(form),
              getFieldItems
            ),
          }),
        });
      })
      .then(() => {
        // The API changed the guest's favorites or topics; refresh them and send the new lists to Braze.
        const syncedFavorites = userFields.favoriteVenues !== undefined;
        const syncedTopics = userFields.interestTopics !== undefined;
        if (submitAction && (syncedFavorites || syncedTopics)) {
          me.fetch().then((user) => {
            if (syncedFavorites) me.trackFavorites('venues');
            if (syncedTopics && Array.isArray(user?.interestTopics)) {
              track('Set Properties', { interestTopics: user.interestTopics });
            }
          });
        }
        setMessage({
          type: 'success',
          body: getTextCopy('successMessage', DEFAULT_SUCCESS_MESSAGE),
        });
      })
      .catch((err) => {
        console.error(err);
        setMessage({
          type: 'error',
          body: err.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const sendToBraze = async () => {
    const fields = Object.fromEntries(
      getFormFields(form)
        .map(({ brazeName, name, value }) => [
          brazeName,
          value || formData[name],
        ])
        .filter(([key, value]) => key && value)
    );

    if (!Object.keys(fields).length) return;

    track('Set Properties', {
      ...fields,
      ...brazeCustomTriggers?.customAttributes,
    });

    if (brazeCustomTriggers?.customEvent) {
      track(brazeCustomTriggers.customEvent);
    }

    if (fields.emailAccepted) {
      createSubscription(fields, DEFAULT_GROUP_ID);
    }

    if (fields.smsAccepted) {
      createSubscription(fields, SMS_GROUP_ID);
    }
  };

  const validate = (fieldName = null, { value } = { value: undefined }) => {
    const fields = Object.fromEntries(
      getFormFields(form).map(({ name }) => {
        return [name, value || formData[name]];
      })
    );

    const validations = getFieldValidations(form);

    const newFieldErrors = Object.keys(fields)
      .filter((key) => !fieldName || fieldName === key)
      .reduce((fieldErrors, key) => {
        const fieldValidations = validations[key];
        if (!fieldValidations?.length) return fieldErrors;

        const error = fieldValidations.find((a) => a.isInvalid(fields));
        return {
          ...fieldErrors,
          [key]: error ? error.message : null,
        };
      }, {});

    const errors = {
      ...fieldErrors,
      ...newFieldErrors,
    };

    setFieldErrors(errors);

    return !Object.values(errors).some(Boolean);
  };

  const renderAsset = () => {
    if (!textBlock?.asset) return null;
    return <Asset asset={textBlock.asset} />;
  };

  const renderTextBlock = () => {
    if (!textBlock) return null;
    const renderTextBlock = {
      ...textBlock,
      asset: null,
    };
    return <TextBlock textBlock={renderTextBlock} />;
  };

  const renderCTA = () => {
    if (loading) return <Loader active inline />;
    else if (message) return renderMessage();
    return (
      <Button type="submit" className={className('submit-button')} inverted>
        {getTextCopy('submitButton', 'Submit Form')}
      </Button>
    );
  };

  const renderMessage = () => {
    if (!message) return null;
    return (
      <div className={className('message-wrapper')}>
        <Message
          positive={message.type === 'success'}
          negative={message.type === 'error'}>
          {message.body}
        </Message>
      </div>
    );
  };

  const getFocusListeners = (fieldName) => {
    return {
      onBlur: (value) => {
        setCurrentFocus(null);
        validate(fieldName, value);
      },
      onFocus: () => setCurrentFocus(fieldName),
    };
  };

  const hasFocus = (fieldName) => currentFocus === fieldName;

  const renderLabel = ({ name, label, required, description }) => (
    <>
      <label htmlFor={name}>{`${label}${required ? ' *' : ''}`}</label>
      {description && (
        <p className={className('field-description')}>{description}</p>
      )}
    </>
  );

  const renderField = (field) => {
    const {
      label,
      type,
      name,
      value,
      required,
      placeholder,
      checkboxContent,
      display,
    } = field;
    const span = isMobile
      ? FIELD_SPANS.full
      : FIELD_SPANS[fieldLayout[name]] || FIELD_SPANS[getDefaultWidth(field)];
    const fieldStyle = {
      gridColumn: `span ${span}`,
      marginBottom: '32px',
    };

    if (type === 'hidden') {
      return <input key={name} type={type} name={name} value={value} />;
    }

    if (['text', 'number', 'email'].includes(type)) {
      return (
        <ControlledInputContainer
          key={name}
          required={required}
          label={renderLabel(field)}
          input={
            <input
              value={formData[name]}
              id={name}
              name={name}
              type={type}
              placeholder={placeholder}
              readOnly={isLocked(name)}
              data-locked={isLocked(name) || undefined}
              onChange={(evt) =>
                setFormData({ ...formData, [name]: evt.target.value })
              }
              {...getFocusListeners(name)}
            />
          }
          hasFocus={hasFocus(name)}
          error={fieldErrors[name]}
          style={fieldStyle}
        />
      );
    }

    if (type === 'textarea') {
      return (
        <FieldContainer style={fieldStyle} key={name} error={fieldErrors[name]}>
          {renderLabel(field)}
          <textarea
            className={className('textarea')}
            value={formData[name] || ''}
            id={name}
            name={name}
            placeholder={placeholder}
            rows={4}
            onChange={(evt) =>
              setFormData({ ...formData, [name]: evt.target.value })
            }
            {...getFocusListeners(name)}
          />
          {fieldErrors[name] && (
            <span className={className('error')}>{fieldErrors[name]}</span>
          )}
        </FieldContainer>
      );
    }

    if (type === 'phone') {
      return (
        <FieldContainer style={fieldStyle} key={name}>
          {renderLabel(field)}
          <PhoneNumberInput
            onChange={(value) => setFormData({ ...formData, [name]: value })}
            initialValue={formData[name]}
            readOnly={isLocked(name)}
            id={name}
            name={name}
            {...getFocusListeners(name)}
            error={fieldErrors[name]}
            placeholder={placeholder}
          />
        </FieldContainer>
      );
    }

    if (type === 'select') {
      return (
        <FieldContainer style={fieldStyle} key={name}>
          {renderLabel(field)}
          <Select
            items={getFieldItems(field)}
            error={fieldErrors[name]}
            setSelectedItem={(item) =>
              setFormData({ ...formData, [name]: item })
            }
            selectedItem={formData[name]}
            {...getFocusListeners(name)}
            placeholder={placeholder}
            readOnly={isLocked(name)}
          />
        </FieldContainer>
      );
    }

    if (type === 'multiselect' && display === 'cards') {
      return (
        <FieldContainer style={fieldStyle} key={name} error={fieldErrors[name]}>
          {renderLabel(field)}
          <CheckboxCards
            name={name}
            items={getFieldItems(field)}
            selectedItems={formData[name] || []}
            setSelectedItems={(items) => {
              setFormData({ ...formData, [name]: items });
              validate(name, { value: items });
            }}
          />
        </FieldContainer>
      );
    }

    if (type === 'multiselect') {
      return (
        <FieldContainer style={fieldStyle} key={name}>
          {renderLabel(field)}
          <MultiSelect
            title={`${label}${required ? ' *' : ''}`}
            items={getFieldItems(field)}
            error={fieldErrors[name]}
            setSelectedItems={(items) => {
              setFormData({ ...formData, [name]: [...items] });
            }}
            selectedItems={[...(formData[name] || [])]}
            {...getFocusListeners(name)}
            placeholder={placeholder}
          />
        </FieldContainer>
      );
    }

    if (type === 'date') {
      return (
        <FieldContainer
          style={fieldStyle}
          {...getFocusListeners(name)}
          key={name}>
          {renderLabel(field)}
          <ModularDatePicker
            date={formData[name]}
            setDate={(date) => setFormData({ ...formData, [name]: date })}
            error={fieldErrors[name]}
            {...getFocusListeners(name)}
            placeholder={placeholder}
            readOnly={isLocked(name)}
            pastOnly={isPastDateField(field)}
          />
        </FieldContainer>
      );
    }

    if (type === 'checkbox') {
      return (
        <div key={name} style={{ gridColumn: fieldStyle.gridColumn }}>
          <Checkbox
            name={name}
            error={fieldErrors[name]}
            checked={formData[name] || false}
            label={<ContentfulRichText field={checkboxContent} />}
            onChange={(e) =>
              setFormData({ ...formData, [name]: e.target.checked })
            }
          />
        </div>
      );
    }
  };

  const renderForm = () => {
    if (authGate.status === 'checking') return <Loader active inline />;
    if (authGate.status === 'anonymous') {
      return (
        <FormLoginGate
          signupUrl={authGate.signupUrl}
          loginUrl={authGate.loginUrl}
          message={getTextCopy('requireLoginMessage')}
        />
      );
    }

    const fields = getFormFields(form);

    return (
      <Form noValidate onSubmit={handleSubmit} className={className('form')}>
        {fields.map((field) => renderField(field))}
        <div className={className('cta')}>{renderCTA()}</div>
      </Form>
    );
  };

  return (
    <div
      className={className('container')}
      id={id}
      {...(backgroundTransparent && {
        style: { background: 'transparent' },
      })}>
      <div className={className('content')}>
        <div className={className('asset')}>{renderAsset()}</div>
        <div
          className={className(
            'form-container',
            authGate.status === 'ready' ? '' : 'gated'
          )}>
          {renderTextBlock()}
          <Spacer size="m" />
          {renderForm()}
        </div>
      </div>
    </div>
  );
};

ModularForm.propTypes = {
  id: PropTypes.string,
  form: PropTypes.object.isRequired,
  brazeTriggers: PropTypes.shape({
    customAttributes: PropTypes.object,
    customEvent: PropTypes.string,
  }),
  background: PropTypes.string,
  handleTrack: PropTypes.func,
};

ModularForm.defaultProps = {
  handleTrack: () => {},
};

export default inject(
  'subscriptions',
  'formSubmission',
  'me',
  'appSession'
)(ModularForm);
